import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ArrowBackIos from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos'
import Refresh from '@mui/icons-material/Refresh'
import ViewList from '@mui/icons-material/ViewList'
import Accessible from '@mui/icons-material/Accessible'
import AirlineSeatFlat from '@mui/icons-material/AirlineSeatFlat'
import DirectionsWalk from '@mui/icons-material/DirectionsWalk'
import PersonOff from '@mui/icons-material/PersonOff'
import API from '../../../services/API'
import {
  QuoteStatusChip,
  useBookingQuoteStatuses,
} from '../components/quoteStatus'

const LEFT_RAIL_WIDTH = 200
const ROW_HEIGHT = 56
const HOUR_PX = { 1: 56, 3: 22, 7: 10 }

// Same colour language as the Vehicle Timeline's duty blocks (default/active/
// complete/problem), plus an "unassigned" state — the thing a coordinator
// scanning this view is usually looking for is which patients still have an
// unplanned journey on the books.
const JOURNEY_STATUS_COLORS = {
  unassigned: { bg: '#FEF3C7', border: '#D97706', text: '#78350F' },
  planned: { bg: '#DBEAFE', border: '#3B82F6', text: '#1E3A8A' },
  complete: { bg: '#DCFCE7', border: '#22C55E', text: '#14532D' },
  problem: { bg: '#E2E8F0', border: '#94A3B8', text: '#475569' },
}

const getJourneyColors = (journey) => {
  if (journey.status === 'Completed') return JOURNEY_STATUS_COLORS.complete
  if (['Cancelled', 'No Show', 'Aborted'].includes(journey.status)) {
    return JOURNEY_STATUS_COLORS.problem
  }
  if (!journey.assigned) return JOURNEY_STATUS_COLORS.unassigned
  return JOURNEY_STATUS_COLORS.planned
}

const MOBILITY_ICONS = {
  walking: <DirectionsWalk sx={{ fontSize: 13 }} />,
  wheelchair: <Accessible sx={{ fontSize: 13 }} />,
  stretcher: <AirlineSeatFlat sx={{ fontSize: 13 }} />,
}

// Positions a block within the visible window as percentage offsets
const blockPosition = (windowStart, windowEnd, start, end) => {
  const total = windowEnd - windowStart
  const from = Math.max(0, new Date(start) - windowStart)
  const to = Math.min(total, new Date(end) - windowStart)
  if (to <= 0 || from >= total) return null
  return {
    left: `${(from / total) * 100}%`,
    width: `${Math.max(0.5, ((to - from) / total) * 100)}%`,
  }
}

const JourneyBlock = ({ journey, position, onClick }) => {
  const colors = getJourneyColors(journey)
  return (
    <Tooltip
      title={`${journey.journeyType || 'Journey'} — ${journey.status}${
        journey.assigned ? '' : ' (unassigned)'
      }`}
    >
      <Box
        onClick={onClick}
        sx={{
          position: 'absolute',
          top: 6,
          height: ROW_HEIGHT - 12,
          ...position,
          backgroundColor: colors.bg,
          border: `2px solid ${colors.border}`,
          borderRadius: 1.5,
          px: 0.75,
          py: 0.25,
          overflow: 'hidden',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minWidth: 40,
          '&:hover': { boxShadow: 2 },
        }}
      >
        <Typography noWrap sx={{ fontSize: '0.7rem', fontWeight: 800, color: colors.text }}>
          {moment(journey.blockStart).format('HH:mm')}
          {journey.duty?.vehicleCallSign ? ` · ${journey.duty.vehicleCallSign}` : ''}
        </Typography>
        <Typography noWrap sx={{ fontSize: '0.66rem', color: colors.text, opacity: 0.85 }}>
          {[journey.pickupName, journey.dropoffName].filter(Boolean).join(' → ') ||
            journey.journeyType ||
            'Journey'}
        </Typography>
      </Box>
    </Tooltip>
  )
}

const PatientTimeline = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialDate = searchParams.get('date')
  const [startDate, setStartDate] = useState(
    initialDate && moment(initialDate, 'YYYYMMDD', true).isValid()
      ? moment(initialDate, 'YYYYMMDD')
      : moment(),
  )
  const [days, setDays] = useState(1)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedJourney, setSelectedJourney] = useState(null)
  const quoteStatuses = useBookingQuoteStatuses(
    selectedJourney?.bookingId ? [selectedJourney.bookingId] : [],
  )
  const selectedJourneyQuoteStatus = selectedJourney?.bookingId
    ? quoteStatuses[String(selectedJourney.bookingId)]
    : null

  const windowStart = useMemo(
    () => moment(startDate).startOf('day').toDate(),
    [startDate],
  )
  const windowEnd = useMemo(
    () => moment(startDate).add(days - 1, 'days').endOf('day').toDate(),
    [startDate, days],
  )

  const loadTimeline = useCallback(() => {
    setLoading(true)
    setError(null)
    API.get(
      `/pts/patient-timeline?start=${moment(windowStart).format('YYYY-MM-DD')}&end=${moment(windowEnd).format('YYYY-MM-DD')}`,
    )
      .then((res) => {
        if (res.status === 200) {
          setData(res.data)
        } else {
          setError(res.data?.error || 'Unable to load the timeline.')
        }
      })
      .catch((e) =>
        setError(e?.response?.data?.error || 'Unable to load the timeline.'),
      )
      .finally(() => setLoading(false))
  }, [windowStart, windowEnd])

  useEffect(() => {
    loadTimeline()
  }, [loadTimeline])

  const timelineWidth = days * 24 * HOUR_PX[days]

  const rows = useMemo(() => {
    if (!data) return []
    return data.patients.map((patient) => ({
      key: String(patient._id),
      patient,
    }))
  }, [data])

  const hourTicks = useMemo(() => {
    const ticks = []
    const stepHours = days === 1 ? 1 : days === 3 ? 3 : 6
    for (let hour = 0; hour < days * 24; hour += stepHours) {
      ticks.push(hour)
    }
    return ticks
  }, [days])

  const nowPosition = useMemo(() => {
    const now = new Date()
    if (now < windowStart || now > windowEnd) return null
    return `${((now - windowStart) / (windowEnd - windowStart)) * 100}%`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowStart, windowEnd, data])

  const unassignedCount = useMemo(
    () =>
      (data?.patients || []).reduce(
        (count, patient) =>
          count + patient.journeys.filter((journey) => !journey.assigned).length,
        0,
      ),
    [data],
  )

  return (
    <Stack spacing={1.5}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ md: 'center' }}
        spacing={1}
      >
        <Typography variant="title2">Patient Timeline</Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <IconButton size="small" onClick={() => setStartDate((prev) => moment(prev).subtract(days, 'days'))}>
            <ArrowBackIos fontSize="small" />
          </IconButton>
          <TextField
            type="date"
            size="small"
            value={moment(startDate).format('YYYY-MM-DD')}
            onChange={(e) => {
              const next = moment(e.target.value, 'YYYY-MM-DD', true)
              if (next.isValid()) setStartDate(next)
            }}
          />
          <IconButton size="small" onClick={() => setStartDate((prev) => moment(prev).add(days, 'days'))}>
            <ArrowForwardIos fontSize="small" />
          </IconButton>
          <Button size="small" onClick={() => setStartDate(moment())}>
            Today
          </Button>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={days}
            onChange={(e, value) => value && setDays(value)}
          >
            <ToggleButton value={1}>Day</ToggleButton>
            <ToggleButton value={3}>3 Days</ToggleButton>
            <ToggleButton value={7}>Week</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={loadTimeline}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewList />}
            onClick={() => navigate('/pts/patients')}
          >
            Patient List
          </Button>
        </Stack>
      </Stack>

      {loading && <LinearProgress />}
      {error && (
        <Alert severity="error" variant="filled">
          {error}
        </Alert>
      )}
      {!loading && !error && unassignedCount > 0 && (
        <Alert severity="warning" sx={{ py: 0.25 }}>
          {unassignedCount} journey{unassignedCount === 1 ? '' : 's'} in this
          window still {unassignedCount === 1 ? 'has' : 'have'} no duty
          assigned (shown in amber).
        </Alert>
      )}

      {data && (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', maxHeight: '58vh', overflow: 'auto' }}>
            {/* Left rail */}
            <Box
              sx={{
                width: LEFT_RAIL_WIDTH,
                flexShrink: 0,
                borderRight: '1px solid',
                borderColor: 'divider',
                position: 'sticky',
                left: 0,
                zIndex: 3,
                backgroundColor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  height: 34,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'background.paper',
                  zIndex: 4,
                }}
              >
                <Typography variant="caption" fontWeight={800}>
                  Patient
                </Typography>
              </Box>
              {rows.map((row) => (
                <Box
                  key={row.key}
                  sx={{
                    height: ROW_HEIGHT,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    px: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {MOBILITY_ICONS[row.patient.mobility]}
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem' }} noWrap>
                      {row.patient.name}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5}>
                    {row.patient.isBariatric && (
                      <Chip size="small" label="Bariatric" sx={{ height: 16, fontSize: '0.6rem', borderRadius: 0.5 }} />
                    )}
                    {row.patient.mustTravelAlone && (
                      <Tooltip title="Must travel alone">
                        <PersonOff sx={{ fontSize: 13, color: 'text.secondary' }} />
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              ))}
              {rows.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No patients have a journey in this window.
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Time grid */}
            <Box sx={{ position: 'relative', width: timelineWidth, flexShrink: 0 }}>
              <Box
                sx={{
                  height: 34,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'background.paper',
                  zIndex: 2,
                }}
              >
                {hourTicks.map((hour) => (
                  <Typography
                    key={hour}
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      position: 'absolute',
                      left: `${(hour / (days * 24)) * 100}%`,
                      top: 8,
                      pl: 0.5,
                      borderLeft: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {days === 1
                      ? `${String(hour).padStart(2, '0')}:00`
                      : moment(windowStart).add(hour, 'hours').format(hour % 24 === 0 ? 'ddd DD' : 'HH:mm')}
                  </Typography>
                ))}
              </Box>

              {hourTicks.map((hour) => (
                <Box
                  key={`line-${hour}`}
                  sx={{
                    position: 'absolute',
                    top: 34,
                    bottom: 0,
                    left: `${(hour / (days * 24)) * 100}%`,
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    opacity: 0.5,
                  }}
                />
              ))}
              {nowPosition && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 34,
                    bottom: 0,
                    left: nowPosition,
                    borderLeft: '2px solid #EF4444',
                    zIndex: 1,
                  }}
                />
              )}

              {rows.map((row) => (
                <Box
                  key={row.key}
                  sx={{
                    position: 'relative',
                    height: ROW_HEIGHT,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {row.patient.journeys.map((journey) => {
                    const position = blockPosition(
                      windowStart,
                      windowEnd,
                      journey.blockStart,
                      journey.blockEnd,
                    )
                    return position ? (
                      <JourneyBlock
                        key={journey._id}
                        journey={journey}
                        position={position}
                        onClick={() => setSelectedJourney({ ...journey, patientName: row.patient.name })}
                      />
                    ) : null
                  })}
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      )}

      {/* Journey detail dialog */}
      <Dialog
        open={!!selectedJourney}
        onClose={() => setSelectedJourney(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {selectedJourney?.patientName}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={selectedJourney?.status} sx={{ borderRadius: 1, fontWeight: 700 }} />
              <QuoteStatusChip
                status={selectedJourneyQuoteStatus}
                warnOnly
                sx={{ fontSize: '0.8125rem', height: 24 }}
              />
              {!selectedJourney?.assigned && (
                <Chip size="small" color="warning" label="No duty assigned" sx={{ borderRadius: 1, fontWeight: 700 }} />
              )}
              {selectedJourney?.duty?.vehicleCallSign && (
                <Chip size="small" label={selectedJourney.duty.vehicleCallSign} sx={{ borderRadius: 1 }} />
              )}
            </Stack>
            <Typography variant="body2">
              {selectedJourney?.journeyType || 'Journey'}
              {selectedJourney?.bookingRef ? ` — ${selectedJourney.bookingRef}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[selectedJourney?.pickupName, selectedJourney?.dropoffName]
                .filter(Boolean)
                .join(' → ') || 'No pickup/dropoff recorded'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedJourney?.anchorTime
                ? moment(selectedJourney.anchorTime).format('ddd DD MMM, HH:mm')
                : 'No time set'}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          {selectedJourney?.bookingId && (
            <Button
              onClick={() => navigate(`/pts/bookings/${selectedJourney.bookingId}`)}
            >
              Open Booking
            </Button>
          )}
          <Button onClick={() => setSelectedJourney(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default PatientTimeline
